import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { completionsAdapter } from './selectors'
import {
  fetchCompletions,
  fetchLatestThread,
  createNewThread,
  createCompletion,
  updateCompletion,
} from './actions'
import {
  ChannelState,
  Completion,
  CompletionDelta,
  PilotFunctionCall,
  PilotThread,
  FunctionCallState,
  FunctionCallProgressMessage,
  CompletionsState,
} from '../../types'

const getInitialCompletionsState = (): Omit<CompletionsState, 'ids' | 'entities'> => ({
  channelState: ChannelState.Disconnected,
  creating: false,
  fetching: false,
  hasNextPage: false,
  activeFunctionCalls: {},
  functionCallProgress: {},
  currentThread: undefined,
})

export const completionsSlice = createSlice({
  name: 'completions',
  initialState: completionsAdapter.getInitialState<CompletionsState>(getInitialCompletionsState()),
  reducers: {
    clearCompletions(state) {
      completionsAdapter.removeAll(state)
      Object.assign(state, getInitialCompletionsState())
    },
    setCurrentThread(state, action: PayloadAction<PilotThread>) {
      state.currentThread = action.payload
    },
    upsertCompletion(state, action: PayloadAction<Completion>) {
      const incomingCompletion = action.payload
      const existingCompletion = state.entities[incomingCompletion.id]

      // If the incoming completion has an assistantResponse, use it
      // Otherwise, preserve the existing assistantResponse from deltas if we have one
      if (!incomingCompletion.assistantResponse && existingCompletion?.assistantResponse) {
        completionsAdapter.upsertOne(state, {
          ...incomingCompletion,
          assistantResponse: existingCompletion.assistantResponse,
        })
      } else {
        completionsAdapter.upsertOne(state, incomingCompletion)
      }
    },
    updateChannelState(state, action: PayloadAction<ChannelState>) {
      state.channelState = action.payload
    },
    appendAssistantCompletionDelta(state, action: PayloadAction<CompletionDelta>) {
      const completion = state.entities[action.payload.id]

      if (completion) {
        let assistantResponse = completion.assistantResponse

        if (assistantResponse) {
          assistantResponse += action.payload.assistantResponseDelta
        } else {
          assistantResponse = action.payload.assistantResponseDelta
        }

        state.entities[action.payload.id] = {
          ...completion,
          assistantResponse,
        }
      }
    },
    addFunctionCall(state, action: PayloadAction<PilotFunctionCall>) {
      const functionCall = action.payload
      state.activeFunctionCalls[functionCall.id] = functionCall

      const completion = state.entities[functionCall.pilotCompletionId]
      if (completion) {
        const existingFunctionCalls = completion.pilotFunctionCalls ?? []
        completion.pilotFunctionCalls = [...existingFunctionCalls, functionCall]
      }
    },
    updateFunctionCallState(
      state,
      action: PayloadAction<{
        id: number
        state: FunctionCallState
        result?: any
        errorMessage?: string
      }>
    ) {
      const { id, state: newState, result, errorMessage } = action.payload
      const functionCall = state.activeFunctionCalls[id]

      if (functionCall) {
        state.activeFunctionCalls[id] = {
          ...functionCall,
          state: newState,
          result,
          errorMessage,
        }

        const completion = state.entities[functionCall.pilotCompletionId]
        if (completion?.pilotFunctionCalls) {
          completion.pilotFunctionCalls = completion.pilotFunctionCalls.map(fc =>
            fc.id === id ? { ...fc, state: newState, result, errorMessage } : fc
          )
        }

        if (newState === FunctionCallState.Completed || newState === FunctionCallState.Failed) {
          delete state.activeFunctionCalls[id]
          delete state.functionCallProgress[id]
        }
      }
    },
    updateFunctionCallProgress(state, action: PayloadAction<FunctionCallProgressMessage>) {
      const { functionCallId, message } = action.payload
      state.functionCallProgress[functionCallId] = message
    },
    clearFunctionCallProgress(state, action: PayloadAction<number>) {
      delete state.functionCallProgress[action.payload]
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCompletions.pending, state => {
      state.fetching = true
    })
    builder.addCase(fetchCompletions.fulfilled, (state, action) => {
      const payload = { ...action.payload } as {
        data: Completion[]
        hasNextPage: boolean
        pilotThread?: PilotThread
      }

      state.fetching = false
      state.hasNextPage = payload.hasNextPage
      completionsAdapter.upsertMany(state, payload.data)

      if (payload.pilotThread) {
        state.currentThread = payload.pilotThread
      }
    })
    builder.addCase(fetchCompletions.rejected, state => {
      state.fetching = false
    })

    builder.addCase(fetchLatestThread.pending, state => {
      state.fetching = true
    })
    builder.addCase(fetchLatestThread.fulfilled, (state, action) => {
      const payload = { ...action.payload } as { data: Completion[]; pilotThread?: PilotThread }

      state.fetching = false
      completionsAdapter.removeAll(state)
      completionsAdapter.upsertMany(state, payload.data)

      if (payload.pilotThread) {
        state.currentThread = payload.pilotThread
      }
    })
    builder.addCase(fetchLatestThread.rejected, state => {
      state.fetching = false
    })

    builder.addCase(createNewThread.fulfilled, (state, action) => {
      const payload = { ...action.payload } as { pilotThread: PilotThread }

      completionsAdapter.removeAll(state)
      state.currentThread = payload.pilotThread
    })

    builder.addCase(createCompletion.pending, state => {
      state.creating = true
    })
    builder.addCase(createCompletion.fulfilled, (state, action) => {
      const payload = { ...action.payload } as { data: Completion }

      state.creating = false
      completionsAdapter.upsertOne(state, payload.data)
    })
    builder.addCase(createCompletion.rejected, state => {
      state.creating = false
    })

    builder.addCase(
      updateCompletion.pending,
      (
        state,
        {
          meta: {
            arg: { id },
          },
        }
      ) => {
        completionsAdapter.upsertOne(state, { id, updating: true } as any)
      }
    )
    builder.addCase(updateCompletion.fulfilled, (state, action) => {
      const payload = { ...action.payload } as { data: Completion }

      completionsAdapter.upsertOne(state, { ...payload.data, updating: false })
    })
    builder.addCase(
      updateCompletion.rejected,
      (
        state,
        {
          meta: {
            arg: { id },
          },
        }
      ) => {
        completionsAdapter.upsertOne(state, { id, updating: false } as any)
      }
    )
  },
})

export default completionsSlice.reducer

export const {
  clearCompletions,
  setCurrentThread,
  appendAssistantCompletionDelta,
  updateChannelState,
  upsertCompletion,
  addFunctionCall,
  updateFunctionCallState,
  updateFunctionCallProgress,
  clearFunctionCallProgress,
} = completionsSlice.actions

export { createNewThread, fetchLatestThread } from './actions'
